// Person index for pedigree data overlays (TGCLOUD-1670).
//
// One place that answers: given a clinical note's `familyMemberId`, which member
// is that, which canvas nodes draw them, and are any of those nodes on screen?
//
// The id space is the trap. `notes.familyMemberId` holds a PLAINTEXT UPN string
// of the form `<familyId>-<upn>` (minted at userController.js:1793-1796), never a
// Mongo _id, and a note attached to the PROBAND carries the family root's
// `proBandId`, which is the same id space with upn 1. Three readers in the
// codebase disagree about this; the create paths do not. See the plan, section
// 2.3. The member-side key is therefore `proBandId ?? familyMemberId`, matching
// what PedigreeChart already does for the linked-record badge (PedigreeChart.jsx:545).
//
// `pid` is NOT identity: normalizeIds renumbers it on every load and proxy clones
// mint synthetic pids. `uid` (the stable _id string) is identity; pids are only
// used at the draw boundary.

/** The key a clinical note uses to address this member. */
export const personKeyOf = (m) =>
  m?.proBandId != null && m.proBandId !== ''
    ? String(m.proBandId)
    : m?.familyMemberId != null
    ? String(m.familyMemberId)
    : null;

const uidOf = (m) => String(m?.uid ?? m?._id ?? '');

/**
 * @param {object} args
 * @param {Array}  args.memberRecords            every member, hidden included
 * @param {Array}  args.canvasMembersWithProxies what is actually drawn, proxies included
 * @param {boolean} args.anonymous               suppress names in labels
 * @returns {{ list: Array, byKey: Map, byUid: Map }}
 */
export function resolvePersons({
  memberRecords = [],
  canvasMembersWithProxies = [],
  anonymous = false
} = {}) {
  // pid -> every canvas node that draws that person. A proxy is a second
  // rendering of ONE person: it carries the same familyMemberId and points at
  // the original with proxyOf. A gap-spotting overlay marks EVERY rendering,
  // deliberately unlike the linked-record badge, which skips proxies to avoid
  // double-marking one record (Canvas.jsx:3690-3695). Here a person who appears
  // twice and is missed on one rendering reads as a gap that is not there.
  const pidsByOwnerPid = new Map();
  for (const node of canvasMembersWithProxies) {
    if (!node || node.pid == null) continue;
    const ownerPid =
      node.isProxy && node.proxyOf != null ? node.proxyOf : node.pid;
    const list = pidsByOwnerPid.get(ownerPid) || [];
    list.push(node.pid);
    pidsByOwnerPid.set(ownerPid, list);
  }

  const list = [];
  const byKey = new Map();
  const byUid = new Map();

  for (const member of memberRecords) {
    if (!member || member.deleted) continue;
    const personKey = personKeyOf(member);
    if (!personKey) continue;

    const pids = pidsByOwnerPid.get(member.pid) || [];
    const name =
      anonymous || member.isHide
        ? ''
        : [member.firstName, member.lastName].filter(Boolean).join(' ').trim();

    const entry = {
      personKey,
      uid: uidOf(member),
      pid: member.pid,
      pids,
      onCanvas: pids.length > 0,
      // The sidebar's rule, applied to the results list: a name only when the
      // deployment is not anonymous and the person is not de-identified,
      // otherwise the member id, which is explicitly non-identifying.
      label: name || personKey,
      member
    };

    list.push(entry);
    byKey.set(personKey, entry);
    // A member can also be addressed by its own familyMemberId when it carries a
    // proBandId; index both so a note written either way still resolves.
    if (member.familyMemberId && String(member.familyMemberId) !== personKey) {
      byKey.set(String(member.familyMemberId), entry);
    }
    if (entry.uid) byUid.set(entry.uid, entry);
  }

  return { list, byKey, byUid };
}

/**
 * Rows the endpoint could not place on a person, plus any this index cannot
 * resolve either. Never silently dropped: a gap-spotting overlay that hides
 * records it failed to read is telling the clinician a family is complete when
 * it is not.
 */
export function unresolvedKeys(facts, personIndex) {
  const fromServer = facts?.unresolved || [];
  const extra = Object.keys(facts?.members || {}).filter(
    (k) => !personIndex?.byKey?.has(k)
  );
  return {
    rows: fromServer,
    keys: extra,
    count: fromServer.length + extra.length
  };
}
